import { Box, Popover } from '@mui/material';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import useAlert from '../../hooks/useAlert';
import { formatQccAmount } from '../../constants/qcc-balance-mock';
import { AlertVariant } from '../../providers/alert.provider';
import type { WalletStatus } from '../../../hooks/useMetaMask';
import { useQccBalances } from '../../../hooks/useQccBalances';
import QccBalanceRows from './qcc-balance-rows';
import QccWaitlistDialog from './qcc-waitlist-dialog';
import {
  QCC_MENU_ACTION_LABEL,
  QccMenuCoinActionKind,
  useQccMenuCoinAction,
} from './use-qcc-menu-coin-action';
import styles from './qcc-header-menu.module.scss';

type QccHeaderMenuProps = {
  account: string;
  anchorEl: HTMLElement | null;
  isDisconnecting: boolean;
  onClose: () => void;
  onDisconnect: () => Promise<void>;
};

type QccWalletButtonProps = {
  account: string | null;
  menuOpen: boolean;
  status: WalletStatus;
  onConnect: () => Promise<void>;
  onOpen: (anchor: HTMLElement) => void;
};

function truncateWalletAddress(account: string): string {
  return `${account.slice(0, 6)}…${account.slice(-4)}`;
}

function coinActionIcon(kind: QccMenuCoinActionKind): string {
  if (kind === 'buy') return 'shopping_cart';
  if (kind === 'setLocation') return 'location_on';
  if (kind === 'onWaitlist') return 'bookmark';
  return 'person_add';
}

export const QccWalletButton: React.FC<QccWalletButtonProps> = ({
  account,
  menuOpen,
  status,
  onConnect,
  onOpen,
}) => {
  if (status === 'restoring') return null;

  const connected = Boolean(account);
  const busy = status === 'connecting' || status === 'verifying' || status === 'disconnecting';
  let label = connected ? 'My Coins' : 'Connect Wallet';
  if (status === 'unverified') label = 'Verify Wallet';
  if (status === 'connecting') label = 'Connecting…';
  if (status === 'verifying') label = 'Verifying…';
  if (status === 'disconnecting') label = 'Disconnecting…';

  return (
    <button
      type="button"
      className={`${styles.button} qc-header-wallet-btn`}
      aria-busy={busy}
      aria-controls={connected ? 'qcc-balance-menu' : undefined}
      aria-expanded={connected ? menuOpen : undefined}
      aria-haspopup={connected ? 'dialog' : undefined}
      disabled={busy}
      onClick={event => {
        if (connected) {
          onOpen(event.currentTarget);
        } else {
          onConnect();
        }
      }}
    >
      <span className={`material-icons ${styles.icon}`} aria-hidden>
        {connected ? 'monetization_on' : 'account_balance_wallet'}
      </span>
      {label}
    </button>
  );
};

const QccHeaderMenu: React.FC<QccHeaderMenuProps> = ({
  account,
  anchorEl,
  isDisconnecting,
  onClose,
  onDisconnect,
}) => {
  const { showAlert } = useAlert();
  const history = useHistory();
  const open =
    Boolean(anchorEl) &&
    typeof anchorEl?.isConnected === 'boolean' &&
    anchorEl.isConnected;
  const coinAction = useQccMenuCoinAction(open);
  const { rows, total, status } = useQccBalances(account);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistVariant, setWaitlistVariant] = useState<'join' | 'onWaitlist'>(
    'join',
  );
  const [joinBusy, setJoinBusy] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const loading = status === 'loading';
  const totalLabel = loading ? '...' : formatQccAmount(total);
  const actionLabel = QCC_MENU_ACTION_LABEL[coinAction.kind];
  const actionDisabled = coinAction.kind === 'joinLoading';

  const copyWalletAddress = async () => {
    try {
      await navigator.clipboard.writeText(account);
      showAlert('Wallet address copied.', AlertVariant.SUCCESS);
    } catch {
      showAlert('Could not copy wallet address.', AlertVariant.ERROR);
    }
  };

  const confirmWaitlistJoin = async () => {
    setJoinBusy(true);
    setJoinError(null);
    try {
      await coinAction.joinWaitlist();
      showAlert('You joined the QuickCam Coin waitlist.', AlertVariant.SUCCESS);
      setWaitlistOpen(false);
    } catch (err: any) {
      setJoinError(
        err?.data?.message ||
          err?.message ||
          'Something went wrong. Please try again.',
      );
    } finally {
      setJoinBusy(false);
    }
  };

  const actionIcon = coinActionIcon(coinAction.kind);

  return (
    <>
      <Popover
        id="qcc-balance-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => onClose()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          className: styles.paper,
          sx: { maxHeight: 'calc(100vh - 32px)', overflow: 'auto' },
        }}
      >
        <Box
          component="section"
          className={styles.content}
          aria-label="QuickCam Coin balances"
        >
          <div className={styles.titleRow}>
            <p className={styles.title}>QuickCam Coins:</p>
            <p className={styles.total}>{totalLabel}</p>
          </div>
          <div className={styles.walletRow}>
            <span className={styles.walletAddress} title={account}>
              {truncateWalletAddress(account)}
            </span>
            <button
              type="button"
              className={styles.copyButton}
              aria-label="Copy connected wallet address"
              onClick={() => copyWalletAddress()}
            >
              <span className="material-icons-outlined" aria-hidden>
                content_copy
              </span>
              Copy Address
            </button>
          </div>
          {coinAction.kind === 'buy' ? (
            <a
              className={styles.coinAction}
              href={coinAction.buyUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() => onClose()}
            >
              <span className="material-icons-outlined" aria-hidden>
                {actionIcon}
              </span>
              {actionLabel}
            </a>
          ) : (
            <button
              type="button"
              className={styles.coinAction}
              disabled={actionDisabled}
              onClick={() => {
                if (coinAction.kind === 'setLocation') {
                  onClose();
                  history.push('/u/settings/location');
                  return;
                }
                onClose();
                setJoinError(null);
                setWaitlistVariant(
                  coinAction.kind === 'onWaitlist' ? 'onWaitlist' : 'join',
                );
                setWaitlistOpen(true);
              }}
            >
              <span className="material-icons-outlined" aria-hidden>
                {actionIcon}
              </span>
              {actionLabel}
            </button>
          )}
          <QccBalanceRows rows={rows} loading={loading} />
          <button
            type="button"
            className={styles.disconnectButton}
            disabled={isDisconnecting}
            onClick={() => {
              onClose();
              onDisconnect();
            }}
          >
            <span className="material-icons-outlined" aria-hidden>
              link_off
            </span>
            {isDisconnecting ? 'Disconnecting…' : 'Disconnect Wallet'}
          </button>
        </Box>
      </Popover>
      <QccWaitlistDialog
        open={waitlistOpen}
        variant={waitlistVariant}
        profileLocation={coinAction.profileLocation}
        busy={joinBusy}
        error={joinError}
        onClose={() => setWaitlistOpen(false)}
        onJoin={() => confirmWaitlistJoin()}
      />
    </>
  );
};

export default QccHeaderMenu;
